import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { Newspaper, Lightning, ArrowSquareOut, Clock, TrendUp, Spinner, MagnifyingGlass, X } from '@phosphor-icons/react';
import Navbar from '../components/Navbar';
import api from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import TeenVestLoader from '../components/TeenVestLoader';

const CATEGORIES = ['All', 'Tech', 'Finance', 'Healthcare', 'Energy', 'Consumer'];
const TICKER_CATEGORIES = {
  Tech: ['AAPL', 'MSFT', 'GOOGL', 'NVDA', 'META', 'AMD', 'INTC', 'NFLX'],
  Finance: ['JPM', 'V', 'PYPL', 'SQ', 'COIN', 'GS', 'BAC'],
  Healthcare: ['JNJ', 'PFE', 'UNH', 'ABBV', 'MRK'],
  Energy: ['XOM', 'CVX', 'NEE', 'ENPH'],
  Consumer: ['AMZN', 'WMT', 'DIS', 'TSLA', 'UBER', 'ABNB'],
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

const NewsCard = ({ article, index, featured = false }) => (
  <motion.a
    href={article.url}
    target="_blank"
    rel="noopener noreferrer"
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.04 }}
    className={`group block glass-card overflow-hidden hover:border-gold/40 transition-all duration-300 ${featured ? 'md:col-span-2 md:row-span-2' : ''}`}
    data-testid={`news-card-${index}`}
  >
    {article.image && (
      <div className={`relative overflow-hidden ${featured ? 'h-56' : 'h-36'}`}>
        <img
          src={article.image}
          alt=""
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
        {article.symbol && (
          <span className="absolute top-3 left-3 px-2 py-0.5 rounded bg-gold/90 text-black text-xs font-mono font-bold">
            ${article.symbol}
          </span>
        )}
      </div>
    )}
    <div className="p-5">
      <h3 className={`font-bold text-white group-hover:text-gold transition-colors leading-snug mb-3 ${featured ? 'text-xl' : 'text-sm'}`}>
        {article.title}
      </h3>
      {featured && article.summary && (
        <p className="text-zinc-400 text-sm leading-relaxed mb-3 line-clamp-2">
          {article.summary}
        </p>
      )}
      <div className="flex items-center justify-between text-xs text-zinc-500">
        <span className="flex items-center gap-1.5">
          <Newspaper size={14} weight="duotone" />
          {article.source}
        </span>
        <span className="flex items-center gap-1.5">
          <Clock size={14} />
          {timeAgo(article.published)}
        </span>
      </div>
    </div>
  </motion.a>
);

const News = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);

  useEffect(() => {
    fetchNewsFeed();
  }, []);

  const fetchNewsFeed = async () => {
    setLoading(true);
    try {
      const res = await api.get('/news/feed?limit=40');
      setArticles(res.data.articles || []);
    } catch {
      toast.error('Failed to load news feed');
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = (cat) => {
    setActiveCategory(cat);
    setSearchResults(null);
    setSearchQuery('');
  };

  const searchTimer = useRef(null);

  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setSearchResults(null);
      setSearchLoading(false);
      if (searchTimer.current) clearTimeout(searchTimer.current);
      return;
    }
    setSearchLoading(true);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      try {
        const symbol = query.toUpperCase().trim();
        const res = await api.get(`/news/stock/${symbol}?limit=10`);
        setSearchResults(res.data);
      } catch {
        setSearchResults({ articles: [], ai_summary: '', symbol: query.toUpperCase() });
      } finally {
        setSearchLoading(false);
      }
    }, 600);
  }, []);

  // Filter by category
  const filteredArticles = activeCategory === 'All'
    ? articles
    : articles.filter(a => TICKER_CATEGORIES[activeCategory]?.includes(a.symbol));

  const displayArticles = searchResults ? searchResults.articles : filteredArticles;

  return (
    <div className="min-h-screen noise-bg" data-testid="news-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Lightning size={32} weight="fill" className="text-gold" />
            <h1 className="text-4xl md:text-5xl font-unbounded font-bold text-white">
              News Feed
            </h1>
          </div>
          <p className="text-lg text-zinc-400">
            Real-time market news from Yahoo Finance + AI insights
          </p>
        </motion.div>

        {/* Search bar */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mb-6">
          <div className="relative max-w-md">
            <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input
              placeholder="Search by ticker (e.g. AAPL, TSLA)..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-12 pr-12 py-5 bg-black/50 border-white/10 focus:border-gold/50 text-white rounded-xl"
              data-testid="news-search-input"
            />
            {searchQuery && (
              <button onClick={() => { setSearchQuery(''); setSearchResults(null); }} className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white" data-testid="news-clear-search">
                <X size={18} />
              </button>
            )}
          </div>
        </motion.div>

        {/* AI Summary */}
        {searchResults?.ai_summary && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6 glass-card p-5 border-l-4 border-l-gold">
            <div className="flex items-center gap-2 mb-2">
              <Lightning size={20} weight="fill" className="text-gold" />
              <span className="text-sm font-bold text-gold">AI Summary for {searchResults.symbol}</span>
            </div>
            <p className="text-zinc-300 text-sm leading-relaxed" data-testid="news-ai-summary">{searchResults.ai_summary}</p>
          </motion.div>
        )}

        {/* Category pills */}
        {!searchResults && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }} className="flex gap-2 mb-8 overflow-x-auto scrollbar-hide">
            {CATEGORIES.map(cat => (
              <Button
                key={cat}
                variant={activeCategory === cat ? 'default' : 'ghost'}
                onClick={() => handleCategoryClick(cat)}
                className={`rounded-full px-5 whitespace-nowrap ${activeCategory === cat ? 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold' : 'bg-white/5 text-zinc-400 hover:bg-white/10 hover:text-white'}`}
                data-testid={`news-category-${cat.toLowerCase()}`}
              >
                {cat}
              </Button>
            ))}
          </motion.div>
        )}

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <TeenVestLoader size={104} label="Loading latest news..." />
          </div>
        ) : searchLoading ? (
          <div className="flex items-center justify-center py-24">
            <TeenVestLoader size={104} label={`Searching news for ${searchQuery.toUpperCase()}...`} />
          </div>
        ) : displayArticles.length === 0 ? (
          <div className="text-center py-24">
            <Newspaper size={48} className="text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400 text-lg">
              {searchResults ? `No news found for "${searchQuery.toUpperCase()}"` : 'No news available for this category'}
            </p>
            <p className="text-zinc-500 text-sm mt-2">Try a different search or category</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {displayArticles.map((article, i) => (
              <NewsCard key={`${article.title}-${i}`} article={article} index={i} featured={i === 0 && !searchResults} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default News;
